import type { NetBuyView, RewardPoolView } from "../types";
import type { SourceTokenView, WalletView } from "../../../state/context-view-types";
import { ArrowUpRightIcon, InfoIcon, LeafIcon } from "../../../components/Icons";
import { explainBlockingReason, shortAddress } from "../../../components/format";
import { TierDisplayNotice } from "../../../components/TierDisplayNotice";
import { UsdAmount } from "../../../components/UsdAmount";
import { formatTokenAmount } from "../../../lib/format-token-amount";
import { useState } from "react";
import { CopyAddressButton } from "../../../components/CopyAddressButton";

interface NetBuyPoolStripProps {
  token: SourceTokenView | null;
  netBuy: NetBuyView;
  pool: RewardPoolView;
  wallet?: Pick<WalletView, "status" | "address">;
  compact?: boolean;
  onOpenStatus(): void;
  onOpenPool(): void;
}

export function NetBuyPoolStrip({ token, netBuy, pool, wallet, onOpenStatus, onOpenPool }: NetBuyPoolStripProps) {
  const [copyStatus, setCopyStatus] = useState("");
  const symbol = netBuy.nativeSymbol ?? "ETH";
  const display = netBuy.tierDisplay;
  const available = display.status !== "UNAVAILABLE" && display.eligibleTiers !== null;
  const sessionRequired = wallet && (symbol === "HYPE" ? !wallet.address : wallet.status !== "CONNECTED");
  const sessionMessage = wallet?.status === "WRONG_NETWORK" ? "Switch network to view net buy" :
    wallet?.status === "SESSION_EXPIRED" || wallet?.status === "SIGNING_IN" || wallet?.address ?
      "Sign in to view net buy" : "Connect your wallet to view net buy";
  const publicFailure = netBuy.blockingReasons.some(reason => reason === "BACKEND_UNAVAILABLE" ||
    reason === "TOKEN_NOT_LAUNCHED" || reason === "CONTRACT_NOT_DEPLOYED");
  const tierSummary = sessionRequired && !publicFailure ? sessionMessage : netBuy.loading ? "Loading eligibility…" : !available
    ? "Eligibility temporarily unavailable"
    : display.eligibleTiers?.length === 0
      ? "Below first tier"
      : display.maxTierUsd === null ? "Tier data available" : `Up to $${display.maxTierUsd.toLocaleString("en-US")} tier`;
  const primaryBlocker = publicFailure ? netBuy.blockingReasons.find(reason => reason === "BACKEND_UNAVAILABLE" ||
    reason === "TOKEN_NOT_LAUNCHED" || reason === "CONTRACT_NOT_DEPLOYED") : netBuy.blockingReasons[0];

  return (
    <section className="metric-strip" aria-label="Financial snapshot">
      <article className="metric-panel net-buy-panel" aria-busy={netBuy.loading}>
        <div className="metric-heading">
          <div>
            <span className="field-eyebrow">NET BUY</span>
            <span className="source-context">{token?.symbol ??
              (netBuy.blockingReasons.includes("BACKEND_UNAVAILABLE") ?
                "BACKEND UNAVAILABLE" : netBuy.blockingReasons.includes("TOKEN_NOT_LAUNCHED") ?
                  "TOKEN NOT LAUNCHED · WAITING FOR LAUNCH" : netBuy.loading ? "LOADING SOURCE TOKEN" : "SOURCE TOKEN UNAVAILABLE")}</span>
          </div>
          <button type="button" className="details-link" onClick={onOpenStatus}>
            Details <ArrowUpRightIcon />
          </button>
        </div>
        <div className="net-buy-values">
          <p className="metric-value">
            {sessionRequired || netBuy.netBuyEth === null ? "—" : formatTokenAmount(netBuy.netBuyEth)}
            <span className="metric-unit">{symbol}</span>
          </p>
          <div className="metric-secondary">
            {sessionRequired ? "Wallet net buy" : netBuy.netBuyUsd === null ? "USD unavailable" : <UsdAmount value={netBuy.netBuyUsd} />}
          </div>
        </div>
        {/* {!sessionRequired && !netBuy.loading && netBuy.netBuyEth !== null ? <details className="usd-precision token-precision">
          <summary>Exact {symbol} value</summary>
          <span>{netBuy.netBuyEth} {symbol}</span>
          <button type="button" onClick={() => {
            if (!navigator.clipboard) { setCopyStatus("Copy unavailable"); return; }
            void navigator.clipboard.writeText(netBuy.netBuyEth!).then(() => setCopyStatus("Copied"), () => setCopyStatus("Copy unavailable"));
          }}>Copy</button>
          <span role="status">{copyStatus}</span>
        </details> : null} */}
        <div className={`eligibility-line ${sessionRequired && !publicFailure ? "session-required" : available && display.status === "CURRENT" && !display.updating ? "healthy" : "blocked"}`}>
          <span className="eligibility-sigil" aria-hidden="true">{sessionRequired && !publicFailure ? "◇" : available ? "◆" : <span>!</span>}</span>
          <div>
            <strong>{tierSummary}</strong>
            {!(sessionRequired && !publicFailure) && !available ? <span>
              {primaryBlocker ? explainBlockingReason(primaryBlocker) : "Waiting for a trusted financial snapshot."}
            </span> : null}
          </div>
        </div>
        {!sessionRequired ? <TierDisplayNotice display={display} /> : null}
        {netBuy.netBuyScope === "PARTIAL_UNVERIFIED" ? (
          <p className="historical-label"><InfoIcon /> Historical {symbol} only — not valid for tier selection</p>
        ) : null}
      </article>

      <article className="metric-panel pool-panel" aria-busy={pool.loading}>
        <div className="metric-heading">
          <div>
            <span className="field-eyebrow">AVAILABLE REWARD POOL</span>
            <span className="source-context">{token?.symbol ?? "—"}</span>
          </div>
          <button type="button" className="details-link" onClick={onOpenPool}>
            Pool details <ArrowUpRightIcon />
          </button>
        </div>
        <div className="pool-orbit" aria-hidden="true"><LeafIcon /></div>
        <p className="metric-value reward-value">
          {pool.availableEth === null ? "—" : pool.availableEth}
          <span className="metric-unit">{symbol}</span>
        </p>
        {/* <p className="pool-caption">
          {pool.loading ? "Loading reward pool…" : pool.available
            ? "Settled backing available for owner-initiated redemption"
            : "Pool state is not currently verified"}
        </p> */}
        {/* {!pool.loading && pool.blockingReasons.length > 0 ?
          <p role="status">{pool.blockingReasons.map(explainBlockingReason).join(" ")}</p> : null} */}
        <dl className="pool-provenance">
          <div><dt>Vault</dt><dd>{shortAddress(pool.vaultAddress)} {pool.vaultAddress ? <CopyAddressButton key={pool.vaultAddress} address={pool.vaultAddress} label="vault address" /> : null}</dd></div>
          <div><dt>Total distributed</dt><dd>{pool.totalPaidEth ?? "—"} {pool.nativeSymbol ?? symbol}</dd></div>
        </dl>
      </article>
    </section>
  );
}
